<template>
  <Dropdown
    ref="dropdownElement"
    class="price-filter-dropdown dropdown--multi"
    caption="Preis"
    label="Preis"
    :disabled="disabled"
    @toggle="onToggle"
  >
    <DropdownItem
      :selected="discountIndeterminate"
      label="Alle Angebote"
      @click="internalDiscounted = undefined"
    >
    </DropdownItem>
    <DropdownItem
      v-if="allowDiscounts"
      :selected="internalDiscounted === true"
      label="Nur rabattierte Angebote"
      :count="discountedCount"
      :disabled="discountedCount === 0"
      @click="internalDiscounted = true"
    >
    </DropdownItem>

    <!-- Price -->
    <div
      v-if="!$config.public.performanceMode.enabled"
      class="price-filter-dropdown__input-item"
    >
      <TextInput
        :id="fromId"
        v-model="minPrice"
        placeholder="0"
        min="0"
        max="100000"
        label="von"
        step="1"
        @keyup.enter="change"
      />
      <div class="price-filter-dropdown__input-appendix">€</div>
    </div>
    <div
      v-if="!$config.public.performanceMode.enabled"
      class="price-filter-dropdown__input-item"
    >
      <TextInput
        :id="toId"
        v-model="maxPrice"
        type="number"
        placeholder="0"
        min="0"
        max="100000"
        label="bis"
        step="1"
        @keyup.enter="change"
      />
      <div class="price-filter-dropdown__input-appendix">€</div>
    </div>

    <template #submit>
      <LinkButton
        tag="button"
        type="button"
        :disabled="!hasChanged"
        primary
        is-wide
        @click="change"
      >
        Übernehmen
      </LinkButton>
    </template>
  </Dropdown>
</template>

<script setup lang="ts">
import { isEqual, isNull, isUndefined } from 'lodash-es';
import { TextInput, LinkButton, Dropdown, DropdownItem } from '@i22/td-longtail-vue-components';
import { withDefaults, ref, computed, watch } from 'vue';
import type { FilterParam } from '~/components/filter/filter-types';

type PriceFilterParamsCustom = { max?: number; min?: number };
export type PriceFilterProps = {
  discountedCount?: number;
  discounted?: boolean;
  onetimePrice?: PriceFilterParamsCustom;
  disabled?: boolean;
  allowDiscounts?: boolean;
  id?: string;
};

const props = withDefaults(defineProps<PriceFilterProps>(), {
  discountedCount: undefined,
  onetimePrice: undefined,
  id: undefined,
  allowDiscounts: true,
  disabled: false,
});

const minPrice = ref<string | undefined>(undefined);
const maxPrice = ref<string | undefined>(undefined);
const internalDiscounted = ref<boolean | undefined>(props.discounted);
const dropdownElement = ref<typeof Dropdown | null>(null);

const emits = defineEmits<{
  (e: 'change', fields: FilterParam): void;
}>();
const discountIndeterminate = computed(
  () => isNull(internalDiscounted.value) || isUndefined(internalDiscounted.value)
);

const cleanMinPrices = computed(() => {
  const min = Number.parseInt(minPrice.value || '', 10) || undefined;
  const max = Number.parseInt(maxPrice.value || '', 10) || undefined;
  if (min !== undefined && max !== undefined && min > 0 && max > 0) {
    return {
      min: Math.min(min, max),
      max: Math.max(min, max),
    };
  }
  if (min > 0) {
    return { min };
  }
  if (max > 0) {
    return { max };
  }

  return undefined;
});

const originalValue = computed(() => {
  const onetimePrice = { min: undefined, max: undefined };
  if (props.onetimePrice) {
    Object.assign(onetimePrice, props.onetimePrice);
  }
  return {
    discounted: props.discounted,
    onetimePrice,
  };
});

const changedValue = computed(() => ({
  discounted: internalDiscounted.value,
  onetimePrice: cleanMinPrices.value,
}));

const hasChanged = computed(() => !isEqual(originalValue.value, changedValue.value));

const fromId = computed(() => (props.id ? `pricesFrom-${props.id}` : 'pricesFrom'));

const toId = computed(() => (props.id ? `pricesTo-${props.id}` : 'pricesTo'));

watch(
  () => props.discounted,
  (val) => {
    internalDiscounted.value = val;
  }
);

watch(
  () => props.onetimePrice,
  (onetimePrice) => {
    if (onetimePrice) {
      minPrice.value = onetimePrice.min?.toString();
      maxPrice.value = onetimePrice.max?.toString();
    } else {
      minPrice.value = undefined;
      maxPrice.value = undefined;
    }
  },
  {
    deep: true,
    immediate: true,
  }
);

const close = () => {
  dropdownElement.value?.close();
};
const change = () => {
  emits('change', changedValue.value);
  close();
};

const reset = () => {
  if (props.onetimePrice) {
    minPrice.value = props.onetimePrice.min?.toString();
    maxPrice.value = props.onetimePrice.max?.toString();
  } else {
    minPrice.value = undefined;
    maxPrice.value = undefined;
  }
  internalDiscounted.value = props.discounted;
};

const onToggle = (isOpened) => {
  if (!isOpened) reset();
};

defineExpose({
  internalDiscounted,
  discountIndeterminate,
  originalValue,
  changedValue,
  hasChanged,
  cleanMinPrices,
  minPrice,
  maxPrice,
  change,
  onToggle,
  reset,
});
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;

.price-filter-dropdown {
  &__input-item {
    padding: $grid-base;
    padding-top: 4px;
    display: flex;
    justify-content: center;
    align-items: flex-end;
    grid-gap: $grid-base;
    border-top: 1px solid var(--color-stroke-subtle);
    cursor: default;

    :deep(.input-wrapper) {
      flex: 1 1 100%;
    }
  }

  &__input-appendix {
    margin-bottom: $grid-base * 0.5;
  }
}
</style>
